import { useEffect, useState } from 'react';

/**
 * RENDERING a fight clock — never defining one.
 *
 * ── The server owns the time ─────────────────────────────────────────────
 *
 * The backend does not send "2:41". It sends the anchor it started from, the
 * time already banked, and — crucially — its OWN current time:
 *
 *     { state, accumulatedMs, startedAtMs, serverNowMs, durationSeconds }
 *
 * From those, elapsed time is `accumulated + (serverNow − startedAt)`, and
 * this hook re-derives that value a few times a second so the digits move.
 * The interval is a repaint, not a count: it never adds a second to anything.
 * If it stops firing — a backgrounded tab throttling timers, a sleeping
 * laptop — the next render is still correct, because the number was computed
 * from the anchor rather than accumulated tick by tick.
 *
 * That is what makes every recovery case the same case. A refresh, a
 * reconnect, a second screen joining mid-fight and a Raspberry Pi rebooting
 * all read the same two numbers and land on the same value.
 *
 * ── Why the offset ───────────────────────────────────────────────────────
 *
 * A hall display's clock may be wildly wrong; a secretary's laptop may be
 * minutes off. So local time is never compared to the server's anchor
 * directly. The difference between `serverNowMs` and `Date.now()` at the
 * moment the payload arrived is held as an offset and applied on every frame,
 * so all screens agree even when none of their clocks do.
 */
const TICK_MS = 200;

export const formatClock = ms => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const elapsedFrom = (timer, offsetMs) => {
    if (!timer) {
        return 0;
    }

    const accumulated = timer.accumulatedMs || 0;
    if (timer.state !== 'RUNNING' || !timer.startedAtMs) {
        return accumulated;
    }

    // Local now, translated into server time, then measured from the anchor.
    const serverNow = Date.now() + offsetMs;

    return accumulated + Math.max(0, serverNow - timer.startedAtMs);
};

const useFightClock = timer => {
    const running = Boolean(timer) && timer.state === 'RUNNING';

    // Captured whenever a payload arrives. Reading `serverNowMs` here — not
    // at tick time — is what keeps the offset a measurement of clock skew
    // rather than of network latency accumulating over the fight.
    const [offsetMs, setOffsetMs] = useState(0);
    const serverNowMs = timer ? timer.serverNowMs : null;

    useEffect(() => {
        if (serverNowMs) {
            setOffsetMs(serverNowMs - Date.now());
        }
    }, [serverNowMs]);

    const [, setFrame] = useState(0);

    useEffect(() => {
        if (!running) {
            return undefined;
        }

        const handle = setInterval(() => setFrame(frame => frame + 1), TICK_MS);

        return () => clearInterval(handle);
    }, [running]);

    const elapsedMs = elapsedFrom(timer, offsetMs);
    const durationSeconds = timer ? timer.durationSeconds : null;
    const hasDuration = typeof durationSeconds === 'number' && durationSeconds > 0;
    const remainingMs = hasDuration ? Math.max(0, durationSeconds * 1000 - elapsedMs) : null;

    return {
        state: timer ? timer.state : 'STOPPED',
        running,
        elapsedMs,
        remainingMs,
        // A configured duration counts down to the bout limit; without one the
        // clock counts up. Nothing invents a limit that the rules do not set.
        displayMs: hasDuration ? remainingMs : elapsedMs,
        expired: hasDuration && remainingMs === 0,
        hasDuration,
    };
};

export default useFightClock;
